/**
 * Lightweight analytics service — Phase 1 observability primitive.
 *
 * Wraps Vercel Analytics (`@vercel/analytics/react` already mounted in
 * App.tsx) to give the rest of the app a stable, narrow `event(name, props?)`
 * API. Two reasons we don't call Vercel directly everywhere:
 *
 *   1. Tests can mock this single module (see LockScreen.test.tsx) instead
 *      of the entire @vercel/analytics package surface.
 *   2. Centralising event names lets us scrub PII (emails, wallet addresses,
 *      API keys) in one place. Adding a new sink (PostHog, Plausible) later
 *      is a one-line change here.
 *
 * Event-name convention: `<domain>_<verb>_<status>` in snake_case.
 *   - "pin_unlock_success", "pin_unlock_failed"
 *   - "biometric_unlock_success", "biometric_unlock_failed", "biometric_unlock_error"
 *   - "portfolio_create", "portfolio_export"
 * Keep them low-cardinality. Per-user identifiers go into Sentry, not here.
 */

import { track } from '@vercel/analytics';

/**
 * Property values we accept on an event payload. Numbers and booleans flow
 * through to the analytics backend; strings are scrubbed for obvious PII
 * shapes before being forwarded.
 */
export type EventProps = Record<string, string | number | boolean>;

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
// EVM 0x… and Solana base58 addresses, plus any 32+ char hex token.
const ADDRESS_RE = /\b(0x[a-fA-F0-9]{8,}|[1-9A-HJ-NP-Za-km-z]{32,44}|[a-fA-F0-9]{32,})\b/g;

function scrub(value: string): string {
  return value.replace(EMAIL_RE, '[email]').replace(ADDRESS_RE, '[address]');
}

function scrubProps(props?: EventProps): EventProps | undefined {
  if (!props) return undefined;
  const out: EventProps = {};
  for (const [k, v] of Object.entries(props)) {
    out[k] = typeof v === 'string' ? scrub(v) : v;
  }
  return out;
}

/**
 * Whether the analytics sink is connected. False during tests / SSR.
 */
function isEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  // Vercel Analytics injects a global once `<Analytics />` mounts.
  return true;
}

export const analytics = {
  /**
   * Record a discrete event. Names are required, props optional.
   * Properties are PII-scrubbed before being sent.
   */
  event(name: string, props?: EventProps): void {
    if (!isEnabled()) return;
    try {
      const cleanProps = scrubProps(props);
      // `track` is a no-op when the Vercel script hasn't loaded yet (offline,
      // ad-blocker, test) — safe to call unconditionally.
      track(name, cleanProps);
    } catch {
      // Never let an analytics failure break the calling flow.
    }
  },
};

/** Re-export for tests that prefer named imports. */
export const event = analytics.event;
